import {css, StyleSheet} from 'aphrodite';
import cn from 'classnames';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {fullPath, setLocationHref, setURLParams} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import isValidEmail from 'parties/users/ValidateEmail';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import {FONT_PROXIMA_15} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import APIUtil from 'util/APIUtil';

import Carousel from './Carousel';
import {COLLAPSIBLE_ITEMS_CONTENT} from './SharedFileMessagingConstants';

interface SectionProps {
  icon: BenchlingIconName;
  isExpanded: boolean;
  onClick: () => void;
  title: string;
  children: JSX.Element;
}
function CollapsibleSection(props: SectionProps): JSX.Element {
  return (
    <div className={css(styles.sectionContainer)}>
      <div className={css(styles.itemTitleBlock)} onClick={props.onClick}>
        <BenchlingIcon icon={props.icon} />
        <h4 className={css(styles.sectionTitle)}>{props.title}</h4>
        <BenchlingIcon icon={props.isExpanded ? 'chevron-down' : 'chevron-right'} />
      </div>
      {props.isExpanded && props.children}
    </div>
  );
}

function EntityDescription({
  entityTypeFromApiId,
  entityTypeFromWorkspace,
}: {
  entityTypeFromApiId: ModelTypeConstants;
  entityTypeFromWorkspace: ModelTypeConstants | null;
}): JSX.Element | null {
  const entityType = entityTypeFromWorkspace ?? entityTypeFromApiId;
  switch (entityType) {
    case ModelTypeConstants.SEQUENCE:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at a <BenchlingIcon icon="helix" /> DNA Sequence. Here are some things you can do
          with it:
        </p>
      );
    case ModelTypeConstants.DNA_OLIGO:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at an <BenchlingIcon icon={FolderItemIcon.OligoV2} /> Oligo. Oligos can be created in
          Benchling Molecular Biology. Here are some things you can do with our toolset:
        </p>
      );
    case ModelTypeConstants.PROTEIN:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at an <BenchlingIcon icon="protein" /> AA Sequence. Here are some things you can do
          with it:
        </p>
      );
    case ModelTypeConstants.ENTRY:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at a <BenchlingIcon icon={FolderItemIcon.Entry} /> Notebook entry. Here are some
          things you can do with it:
        </p>
      );
    case ModelTypeConstants.PROTOCOL:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at a <BenchlingIcon icon={ModelTypeConstants.PROTOCOL} /> Protocol. Here are some
          things you can do with it:
        </p>
      );
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at a <BenchlingIcon icon="asterisk" /> Custom entity. Here are some things you can do
          with Benchling:
        </p>
      );
    case ModelTypeConstants.CHAT_SESSION:
      return (
        <p className={css(styles.panelDescription)}>
          You're looking at an <BenchlingIcon icon="chat" /> AI Chat. Here are some things you can do with it:
        </p>
      );
    default:
      return null;
  }
}
const mapWorkspaceStateToProps = (
  workspaceStore: typeof WorkspaceStore,
  props: {
    entityTypeFromApiId: ModelTypeConstants;
  }
): {entityTypeFromWorkspace: ModelTypeConstants | null} => ({
  entityTypeFromWorkspace: workspaceStore.getState().activeFileType,
});
const ConnectedEntityDescription = WorkspaceStore.connect(mapWorkspaceStateToProps)(EntityDescription);

/**
 * This is the sidepanel that is shown when a user clicks on a share link.
 * It is used to display the entity type and potential options for how to use the entity.
 * It is also used to display the carousel of items and a signup section.
 * @param useSidepanelStyling - whether to use existing sidepanel CSS styling (useful to disable if you are already in a sidepanel)
 */
export default function SharedFileSidepanel({
  useSidepanelStyling,
}: {
  useSidepanelStyling: boolean;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [expandedSection, setExpandedSection] = useState<number | null>(0);
  const entityType = APIUtil.apiIdToType(window.bootstrap!.sharedFolderItemAPIId!);

  return (
    <div className={cn(css(styles.panel), useSidepanelStyling ? 'sidepanel' : '')}>
      <h3>Welcome to Benchling!</h3>
      <ConnectedEntityDescription entityTypeFromApiId={entityType} />
      {COLLAPSIBLE_ITEMS_CONTENT[entityType]!.map((item, i) => (
        <CollapsibleSection
          key={item.title}
          title={item.title}
          icon={item.icon}
          isExpanded={expandedSection === i}
          onClick={() => {
            setExpandedSection(expandedSection === i ? null : i);
          }}
        >
          <div className={css(styles.carouselContainer)}>
            <Carousel items={item.carouselItems} imageWidth={300} imageHeight={115} />
          </div>
        </CollapsibleSection>
      ))}
      <div className={css(styles.signupSection)}>
        <h4>Create account</h4>
        <div className="u-flex-display u-small-spacing-bottom">
          <input
            className={css(styles.emailInput)}
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
          <Button
            className={css(styles.emailSubmitButton)}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={() => {
              analytics.track('benchling', 'initiated_signup', {
                email,
                signup_source: 'signed out view',
                source_component: 'SharedSequenceSidepanel',
              });
              setLocationHref(
                setURLParams('/signup', {
                  next: fullPath(),
                  email: isValidEmail(email) ? email : '',
                })
              );
            }}
          >
            Get started
          </Button>
        </div>
        <div className="u-lr-split">
          <a href={setURLParams('/ext/google-oauth/begin', {next: fullPath()})}>Sign up with Google</a>
          <a href={setURLParams('/signin', {next: fullPath()})}>I already have an account</a>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: GRAY_LIGHTER,
    left: 0,
    padding: '0 20px',
  },
  panelDescription: {
    ...FONT_PROXIMA_15,
    color: GRAY_DARKER,
  },
  itemTitleBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: BLUE_LIGHT,
    ...FONT_PROXIMA_15,
    cursor: 'pointer',
  },
  sectionContainer: {
    margin: '15px 0',
  },
  sectionTitle: {
    marginRight: 'auto',
    marginLeft: '5px',
  },
  signupSection: {
    marginTop: 'auto',
    marginBottom: '30px',
  },
  emailInput: {
    height: '40px',
    padding: '10px 16px',
    borderRadius: '4px 0 0 4px',
    fontSize: '16px',
    lineHeight: 1.5,
  },
  emailSubmitButton: {
    width: '140px',
    height: '40px',
    borderRadius: '0 4px 4px 0',
    fontSize: '15px',
  },
  carouselContainer: {
    marginLeft: '15px',
  },
});
